
























































































































































































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import moment from "moment";
import UserScheduleGrid from "./UserScheduleGrid.vue";
import TimeOffRequestTab from "./components/TimeOffRequestTab.vue";
import { Schedule, ScheduleStatus, ScheduleTimeOff, Vacation } from "@/api";
import { Pie } from "vue-chartjs/legacy";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
);

@Component({
  components: {
    UserScheduleGrid,
    TimeOffRequestTab,
    Pie,
  },
})
export default class UserScheduleLayout extends Vue {
  activeTab = 0;
  biddingShiftId: number | null = null;
  bidSuccessSnackbar = false;

  filters = {
    startDate: "",
    endDate: "",
  };

  startDateMenu = false;
  endDateMenu = false;

  viewMode: "week" | "month" = "month";
  currentDate = moment();

  get generatedDays() {
    const days = [];
    let start =
      this.viewMode === "week"
        ? this.currentDate.clone().startOf("week")
        : this.currentDate.clone().startOf("month");

    if (this.filters.startDate) {
      start = moment(this.filters.startDate);
    }

    let end =
      this.viewMode === "week"
        ? this.currentDate.clone().endOf("week")
        : this.currentDate.clone().endOf("month");

    if (this.filters.endDate) {
      end = moment(this.filters.endDate);
    } else if (this.filters.startDate) {
      end =
        this.viewMode === "week"
          ? start.clone().endOf("week")
          : start.clone().endOf("month");
    }

    const hasFilters = !!this.filters.startDate || !!this.filters.endDate;
    const spansMultipleMonths = start.month() !== end.month();
    const showMonth = hasFilters || spansMultipleMonths;

    const current = start.clone();
    while (current.isSameOrBefore(end, "day")) {
      const fullDateStr = current.format("YYYY-MM-DD");
      days.push({
        name: current.format("ddd").toUpperCase(),
        month: current.format("MMM").toUpperCase(),
        showMonth: showMonth,
        date: current.format("D"),
        fullDate: fullDateStr,
      });
      current.add(1, "day");
    }
    return days;
  }

  get schedules() {
    let list = this.$store.state.schedule.schedules || [];

    // Only fetch schedule for the current logged in user
    const selfEmployee = this.$store.getters["getCurrentEmployeeSelf"];
    if (selfEmployee) {
      list = list.filter((s: Schedule) => s.assignee === selfEmployee.id);
    }

    if (this.filters.startDate) {
      list = list.filter((s: Schedule) => s.date >= this.filters.startDate);
    }
    if (this.filters.endDate) {
      list = list.filter((s: Schedule) => s.date <= this.filters.endDate);
    }

    return list;
  }

  get mappedSchedules() {
    return this.schedules.map((s: Schedule) => ({
      employeeId: s.assignee || 0,
      date: s.date,
      title: s.facilityName || "Shift",
      color: "",
      data: s,
      startTime: s.startTime,
      endTime: s.endTime,
    }));
  }

  get openShifts() {
    const allSchedules: Schedule[] =
      this.$store.state.schedule.openShifts || [];
    return allSchedules
      .filter((s: Schedule) => !s.assignee || s.status === "OPEN")
      .slice(0, 10)
      .map((s: Schedule) => ({
        ...s,
        departmentName: "",
        departmentColor: "#2196f3",
      }));
  }

  get chartData() {
    return this.$store.state.schedule.chartData;
  }

  get scheduleSettings() {
    return this.$store.getters["schedule/scheduleSettings"];
  }

  get weeklyHoursTarget(): number {
    return this.scheduleSettings?.settings?.MaxHourPerWeek ?? 40;
  }

  get totalScheduledHours() {
    return this.chartData?.schedule ?? 0;
  }

  /** Current-week start/end helpers used by summary stats */
  get currentWeekStart(): string {
    return moment()
      .startOf("week")
      .format("YYYY-MM-DD");
  }

  get currentWeekEnd(): string {
    return moment()
      .endOf("week")
      .format("YYYY-MM-DD");
  }

  get selfEmployee() {
    return this.$store.getters["getCurrentEmployeeSelf"];
  }

  /** Call-off count: CANCELLED schedules for current user in the current week */
  get callOffCount(): number {
    const allSchedules: Schedule[] = this.$store.state.schedule.schedules || [];
    const selfId = this.selfEmployee?.id;
    return allSchedules.filter(
      (s: Schedule) =>
        s.status === ScheduleStatus.CANCELLED &&
        (!selfId || s.assignee === selfId) &&
        s.date >= this.currentWeekStart &&
        s.date <= this.currentWeekEnd,
    ).length;
  }

  /** Time-off count: accepted requests for current user in the current week */
  get timeOffCount(): number {
    const requests: ScheduleTimeOff[] =
      this.$store.state.schedule.timeOffRequests || [];
    const selfId = this.selfEmployee?.id;
    return requests.filter(
      (r: ScheduleTimeOff) =>
        r.status === "accepted" &&
        (!selfId || r.employeeId === selfId) &&
        r.date >= this.currentWeekStart &&
        r.date <= this.currentWeekEnd,
    ).length;
  }

  /** Vacation count: vacation entries for current user in the current week */
  get vacationCount(): number {
    const vacations: Vacation[] = this.$store.state.vacation.vacations || [];
    const selfId = this.selfEmployee?.id;
    return vacations.filter(
      (v: Vacation) =>
        (!selfId || v.employeeId === selfId) &&
        v.date >= this.currentWeekStart &&
        v.date <= this.currentWeekEnd,
    ).length;
  }

  get timeSummaryStats() {
    const scheduled = this.chartData?.schedule ?? 0;
    const transferred = this.chartData?.transferred ?? 0;
    return [
      {
        label: "Scheduled Hours",
        value: scheduled,
        color: "#2196f3",
        unit: "hrs",
      },
      {
        label: "Transferred Hours",
        value: transferred,
        color: "#ff9800",
        unit: "hrs",
      },
      {
        label: "Call-offs",
        value: this.callOffCount,
        color: "#f44336",
        unit: "shift(s)",
      },
      {
        label: "Time Off",
        value: this.timeOffCount,
        color: "#9c27b0",
        unit: "day(s)",
      },
      {
        label: "Vacation",
        value: this.vacationCount,
        color: "#4caf50",
        unit: "day(s)",
      },
    ];
  }

  get timeSummaryChartData() {
    const scheduled = this.chartData?.schedule ?? 0;
    const transferred = this.chartData?.transferred ?? 0;
    const total = scheduled + transferred;
    const hasData = total > 0;
    if (!hasData) {
      return {
        labels: ["Remaining"],
        datasets: [
          { backgroundColor: ["#e0e0e0"], data: [this.weeklyHoursTarget] },
        ],
      };
    }
    return {
      labels: ["Scheduled", "Transferred", "Remaining"],
      datasets: [
        {
          backgroundColor: ["#2196f3", "#ff9800", "#e0e0e0"],
          data: [
            scheduled,
            transferred,
            Math.max(this.weeklyHoursTarget - total, 0),
          ],
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    };
  }

  timeSummaryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
      datalabels: { display: false },
    },
  };

  @Watch("filters.startDate")
  @Watch("filters.endDate")
  @Watch("$store.state.schedule.lastSocketUpdate")
  onDateFilterChange() {
    this.fetchData();
  }

  @Watch("activeTab")
  onActiveTabChange(tab: number) {
    if (tab === 1) {
      this.fetchOpenShifts();
    }
  }

  mounted() {
    this.fetchData();
    this.fetchOpenShifts();
    this.$store.dispatch("schedule/fetchScheduleSettings").catch(() => {
      /* ignore */
    });
  }

  handleViewModeChange() {
    if (this.filters.startDate && !this.filters.endDate) {
      this.filters.endDate =
        this.viewMode === "week"
          ? moment(this.filters.startDate)
              .endOf("week")
              .format("YYYY-MM-DD")
          : moment(this.filters.startDate)
              .endOf("month")
              .format("YYYY-MM-DD");
    }

    if (!this.filters.startDate && this.filters.endDate) {
      this.filters.startDate =
        this.viewMode === "week"
          ? moment(this.filters.endDate)
              .startOf("week")
              .format("YYYY-MM-DD")
          : moment(this.filters.endDate)
              .startOf("month")
              .format("YYYY-MM-DD");
    }

    this.fetchData();
  }

  async fetchData() {
    try {
      const days = this.generatedDays;
      const start = days.length > 0 ? days[0].fullDate : undefined;
      const end = days.length > 0 ? days[days.length - 1].fullDate : undefined;

      // Chart / summary data always reflects the current week
      const currentWeekStart = moment()
        .startOf("week")
        .format("YYYY-MM-DD");
      const currentWeekEnd = moment()
        .endOf("week")
        .format("YYYY-MM-DD");

      await Promise.all([
        this.$store.dispatch("schedule/fetchSchedules", { start, end }),
        this.$store.dispatch("schedule/fetchChartData", {
          start: currentWeekStart,
          end: currentWeekEnd,
        }),
        this.$store.dispatch("schedule/fetchTimeOffRequests", {
          startDate: currentWeekStart,
          endDate: currentWeekEnd,
        }),
        this.$store.dispatch("vacation/fetchVacations", {
          startDate: currentWeekStart,
          endDate: currentWeekEnd,
        }),
      ]);
    } catch (error) {
      console.error("Error fetching schedules:", error);
    }
  }

  async fetchOpenShifts() {
    try {
      await this.$store.dispatch("schedule/fetchOpenShifts");
    } catch (error) {
      console.error("Error fetching open shifts:", error);
    }
  }

  get employees() {
    const selfEmployee = this.$store.getters["getCurrentEmployeeSelf"];
    if (!selfEmployee) return [];

    return [
      {
        ...selfEmployee,
        text: `${selfEmployee.firstName} ${selfEmployee.lastName}`,
      },
    ];
  }

  get filteredTimeOffRequests() {
    const selfEmployee = this.$store.getters["getCurrentEmployeeSelf"];
    const allRequests = this.$store.state.schedule.timeOffRequests || [];

    const days = this.generatedDays;
    const start = days.length > 0 ? days[0].fullDate : null;
    const end = days.length > 0 ? days[days.length - 1].fullDate : null;

    // eslint-disable-next-line
    return allRequests.filter((r: any) => {
      if (selfEmployee && r.employeeId !== selfEmployee.id) return false;
      if (start && r.date < start) return false;
      if (end && r.date > end) return false;
      return true;
    });
  }

  formatShiftDate(dateStr: string): string {
    const d = moment(dateStr);
    if (d.isSame(moment(), "day")) return "Today";
    if (d.isSame(moment().add(1, "day"), "day")) return "Tomorrow";
    return d.format("ddd, MMM D");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async bidForShift(shift: any) {
    this.biddingShiftId = shift.id;
    try {
      await this.$store.dispatch("schedule/addShiftBid", {
        scheduleId: shift.id,
        shiftBid: { scheduleId: shift.id },
      });
      this.bidSuccessSnackbar = true;
      await this.fetchOpenShifts();
    } catch (error) {
      console.error("Failed to submit bid:", error);
    } finally {
      this.biddingShiftId = null;
    }
  }
}
